import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, CalendarClock } from "lucide-react";
import { useLive, usePrematch } from "@/hooks/useAtlas";
import { LiveMatchCard } from "@/components/cards/LiveMatchCard";
import { PrematchCard } from "@/components/cards/PrematchCard";
import { LoadingCards, EmptyState, ErrorState } from "@/components/common/States";
import { lifecycleOf } from "@/presentation/resultPresentation";
import { useI18n } from "@/i18n";

function PublicHeader({ title, subtitle, live }: { title: string; subtitle: string; live?: boolean }) {
  return (
    <div className="mx-auto max-w-7xl px-4 pb-6 pt-14 lg:px-8 lg:pt-16">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-2.5">
          {live && <span className="inline-flex items-center gap-1.5 rounded-full border border-live/30 bg-live/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-live-400"><span className="h-2 w-2 rounded-full bg-live animate-live-pulse" /> LIVE</span>}
          <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{title}</h1>
        </div>
        <p className="mt-3 max-w-2xl text-base text-muted-foreground">{subtitle}</p>
      </motion.div>
    </div>
  );
}

function LoginBanner() {
  const { d, lang } = useI18n();
  return (
    <div className="mx-auto mb-8 max-w-7xl px-4 lg:px-8">
      <div className="panel flex flex-col items-start justify-between gap-3 p-5 sm:flex-row sm:items-center">
        <p className="text-sm text-muted-foreground">{lang === "tr" ? "Tam zekâ, geçmiş ve ATLAS asistanı için hesabınızı bağlayın." : "Connect your account for full intelligence, history and the ATLAS assistant."}</p>
        <div className="flex gap-2">
          <Link to="/login" className="rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-foreground transition hover:border-white/25">{d.nav.login}</Link>
          <Link to="/register" className="rounded-full bg-atlas-500 px-4 py-2 text-sm font-bold text-atlas-ink transition hover:shadow-glow">{d.nav.register}</Link>
        </div>
      </div>
    </div>
  );
}

export function LivePublic() {
  const { d, lang } = useI18n();
  const { data, isLoading, isError, error, refetch } = useLive(24);
  const rows = (data?.rows || []).filter((r) => lifecycleOf(r.status, r.won) === "PENDING").slice(0, 9);
  return (
    <div data-testid="public-live-page">
      <PublicHeader live title={d.nav.live} subtitle={lang === "tr" ? "ATLAS'ın şu anda sahada gördüğü canlı market fırsatları." : "Live market opportunities ATLAS is seeing on the pitch right now."} />
      <LoginBanner />
      <div className="mx-auto max-w-7xl px-4 pb-20 lg:px-8">
        {isLoading ? <LoadingCards /> : isError ? <ErrorState error={error as any} onRetry={refetch} /> :
          rows.length === 0 ? <EmptyState title={d.states.emptyLiveTitle} hint={d.states.emptyLiveHint} icon={<Radio className="h-6 w-6" />} /> :
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{rows.map((s, i) => <LiveMatchCard key={s.id} s={s} index={i} />)}</div>}
      </div>
    </div>
  );
}

export function PrematchPublic() {
  const { d, lang } = useI18n();
  const { data, isLoading, isError, error, refetch } = usePrematch(24);
  const rows = (data?.rows || []).slice(0, 9);
  return (
    <div data-testid="public-prematch-page">
      <PublicHeader title={d.nav.prematch} subtitle={lang === "tr" ? "Kanıta dayalı maç öncesi zekâ; güven, edge ve açıklama ile." : "Evidence-driven prematch intelligence with confidence, edge and explanation."} />
      <LoginBanner />
      <div className="mx-auto max-w-7xl px-4 pb-20 lg:px-8">
        {isLoading ? <LoadingCards /> : isError ? <ErrorState error={error as any} onRetry={refetch} /> :
          rows.length === 0 ? <EmptyState title={d.states.emptyPrematch} icon={<CalendarClock className="h-6 w-6" />} /> :
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{rows.map((f, i) => <PrematchCard key={f.fixture_key} f={f} index={i} />)}</div>}
      </div>
    </div>
  );
}
